import { isEmpty } from "lodash";

import { FetchedWasteTypes } from "./api/sanity/types";
import { compareNullableStrings } from "./sorting";

export function sortByOrderRank(
  items: string[],
  sanityWasteTypes: FetchedWasteTypes,
): string[];

export function sortByOrderRank<T>(
  items: T[],
  sanityWasteTypes: FetchedWasteTypes,
  codeKey: keyof T,
): T[];

export function sortByOrderRank<T>(
  items: T[],
  sanityWasteTypes: FetchedWasteTypes,
  codeKey: string,
): T[];

/**
 * Sorts an array of waste type codes or objects with a (possibly nested) waste type code,
 * based on the order rank of their waste type category in Sanity.
 *
 * If sorting objects, you must pass the `codeKey`, which can be flat (`"code"`) or nested (`"wasteType.code"`).
 */
export function sortByOrderRank<T>(
  items: string[] | T[],
  sanityWasteTypes: FetchedWasteTypes,
  codeKey?: keyof T | string,
): string[] | T[] {
  const getOrderRank = (code: string): string =>
    sanityWasteTypes.find((type) => type.id === code)?.wasteTypeCategory
      ?.orderRank ?? "";

  const compare = (a: string, b: string) => {
    return compareNullableStrings(getOrderRank(a), getOrderRank(b));
  };

  if (isEmpty(items) || typeof items[0] === "string") {
    return [...(items as string[])].sort(compare);
  }

  if (!codeKey) {
    throw new Error(
      "Missing `codeKey` for sorting objects by waste type code.",
    );
  }

  const getNestedValue = (obj: any, path: string) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);

  return [...(items as T[])].sort((a, b) => {
    const codeA = String(getNestedValue(a, codeKey as string));
    const codeB = String(getNestedValue(b, codeKey as string));
    return compare(codeA, codeB);
  });
}
